/**
 * Office slice — owns the office upgrade state (cleaning, fountain, coffee
 * machine, break room, plants, restaurant tickets, gym membership).
 *
 * The office itself is "unlocked" implicitly once the player has hired
 * 3+ support roles OR purchased inv-office-01 (see `isOfficeUnlocked`
 * helper in data/officeUpgrades.js). Once unlocked, suppliers start
 * sending opportunistic email proposals via `maybeFireOfficeProposal`.
 *
 * Each owned upgrade adds a small monthly charge AND a permanent
 * satisfaction boost on drivers + support staff (read by other slices
 * via the `getOfficeSatisfactionBoost` aggregator).
 */
import {
  OFFICE_UPGRADES,
  OFFICE_UPGRADE_BY_ID,
  getOfficeUpgradesMonthlyCost,
  getOfficeSatisfactionBoost,
  isOfficeUnlocked,
  pickNextOfficeProposal,
} from '../../data/officeUpgrades';
import { SUPPORT_ROLES } from '../../data/supportRoles';

// Tunables for the opportunistic proposals scheduler.
const FIRST_PROPOSAL_DELAY_DAYS = 7;       // after office unlock, wait 1 week
const PROPOSAL_COOLDOWN_DAYS = 30;          // ~ once per in-game month
const PROPOSAL_RANDOM_JITTER_DAYS = 15;     // ±15 days variance

// 18/06/2026 — Compte à rebours siège social (demande user).
export const HQ_DEADLINE_DAYS = 45;
export const HQ_REMINDER_DAYS = [30, 15, 7, 2];


export const createOfficeSlice = (set, get) => ({
  // Bureau dédié unlocked state — cached so we know the exact day at
  // which it switched on (used as base for the next-proposal scheduler).
  officeUnlockedAt: null,
  /** { [upgradeId]: { boughtAt: dayNumber } } */
  officeUpgrades: {},
  /** Day at which the next office supplier proposal should fire. */
  nextOfficeProposalDay: null,

  // 18/06/2026 — Compte à rebours siège social (demande user) : à 3
  // fonctions support recrutées SANS siège investi, l'apprenant a 45
  // jours pour investir (rappels J-30/J-15/J-7/J-2), sinon la dernière
  // recrue support démissionne et le 3e poste est bloqué jusqu'à
  // l'investissement.
  hqDeadlineDay: null,
  hqRemindersSent: {},
  hqResignationHappened: false,

  // ---- public actions ----

  /**
   * 18/06/2026 — Daily tick : gère le compte à rebours « investissez dans
   * le siège social ». Auto-armé si 3+ supports sans siège (couvre aussi
   * la désactivation de l'investissement), rappels, puis démission.
   */
  checkHqDeadline: () => {
    const state = get();
    const hqActive = !!(state.activeInvestments || {})['inv-office-01'];
    const supportCount = Object.keys(state.hiredSupport || {}).length;
    const today = state.totalDays || 0;

    if (hqActive) {
      // Siège investi → tout est réglé, on nettoie les flags.
      if (state.hqDeadlineDay != null || state.hqResignationHappened) {
        set({ hqDeadlineDay: null, hqRemindersSent: {}, hqResignationHappened: false });
      }
      return;
    }

    if (supportCount < 3) {
      // Repassé sous 3 supports (licenciement, démission) → on désarme.
      if (state.hqDeadlineDay != null) {
        set({ hqDeadlineDay: null, hqRemindersSent: {} });
      }
      return;
    }

    // 3+ supports sans siège : armement auto (filet de sécurité — le cas
    // nominal est armé immédiatement par supportSlice.hireSupport).
    if (state.hqDeadlineDay == null) {
      set({ hqDeadlineDay: today + HQ_DEADLINE_DAYS, hqRemindersSent: {} });
      get()._pushHqDeadlineAnnouncement?.(today + HQ_DEADLINE_DAYS);
      return;
    }

    const daysLeft = state.hqDeadlineDay - today;

    // Rappels aux jalons J-30 / J-15 / J-7 / J-2.
    for (const r of HQ_REMINDER_DAYS) {
      if (daysLeft <= r && daysLeft > 0 && !(state.hqRemindersSent || {})[r]) {
        try {
          const push = get().pushCustomMessage;
          if (typeof push === 'function') {
            push({
              senderType: 'narrator',
              senderName: 'Direction Générale',
              tone: daysLeft <= 7 ? 'urgent' : 'neutral',
              kind: 'info',
              subject: `🏢 Siège social — plus que ${daysLeft} jour${daysLeft > 1 ? 's' : ''} pour investir`,
              body:
                `Rappel : vos 3 fonctions support travaillent toujours sans véritable siège social. ` +
                `Il vous reste ${daysLeft} jour${daysLeft > 1 ? 's' : ''} pour réaliser l'investissement ` +
                `« Siège social / Bureau dédié » (onglet Fonctions support).\n\n` +
                `Sans locaux adaptés à l'échéance, votre dernière recrue support démissionnera.`,
              ttlDays: Math.max(3, daysLeft),
              payload: { kind: 'hq_deadline_reminder', daysLeft },
            });
          }
        } catch (err) { console.warn('[office] hq reminder push failed:', err); }
        set({ hqRemindersSent: { ...(state.hqRemindersSent || {}), [r]: true } });
        break;
      }
    }

    // Échéance dépassée → démission de la dernière recrue support.
    if (daysLeft <= 0) {
      const hired = state.hiredSupport || {};
      const lastRoleId = Object.keys(hired).reduce((best, id) => {
        if (!best) return id;
        return (hired[id]?.hiredAt || 0) >= (hired[best]?.hiredAt || 0) ? id : best;
      }, null);
      const role = SUPPORT_ROLES.find(r => r.id === lastRoleId);
      const next = { ...hired };
      if (lastRoleId) delete next[lastRoleId];
      set({
        hiredSupport: next,
        hqDeadlineDay: null,
        hqRemindersSent: {},
        hqResignationHappened: true,
      });
      try {
        const push = get().pushCustomMessage;
        if (typeof push === 'function') {
          push({
            senderType: 'support',
            senderName: role?.name || 'Fonction support',
            tone: 'urgent',
            kind: 'info',
            subject: `📤 Démission — ${role?.name || 'fonction support'} (${role?.role || 'support'})`,
            body:
              "Je n'ai pas les conditions nécessaires pour travailler, merci d'investir avant de me recontacter.\n\n" +
              `${role?.name || ''}`,
            ttlDays: 30,
            payload: { kind: 'hq_resignation', roleId: lastRoleId },
          });
        }
      } catch (err) { console.warn('[office] hq resignation push failed:', err); }
    }
  },

  /** Message d'annonce du délai de 45 jours (appelé à l'armement). */
  _pushHqDeadlineAnnouncement: (deadlineDay) => {
    try {
      const push = get().pushCustomMessage;
      if (typeof push !== 'function') return;
      push({
        senderType: 'narrator',
        senderName: 'Direction Générale',
        tone: 'neutral',
        kind: 'info',
        subject: '🏢 3 fonctions support recrutées — un siège social s\'impose (45 jours)',
        body:
          "Votre équipe support a atteint 3 personnes : elle ne peut plus travailler durablement dans la cabane du dépôt.\n\n" +
          "Vous disposez de **45 jours** pour investir dans le « Siège social / Bureau dédié » " +
          "(onglet Fonctions support — 18 000 € + charges mensuelles + loyer bureau).\n\n" +
          `Sans investissement d'ici le jour J${deadlineDay}, votre dernière recrue support démissionnera.`,
        ttlDays: 45,
        payload: { kind: 'hq_deadline_start', deadlineDay },
      });
    } catch (err) { console.warn('[office] hq announcement push failed:', err); }
  },

  /**
   * Idempotent : check whether the office is unlocked given the current
   * state. If so AND `officeUnlockedAt` is still null, flag it and schedule
   * the first supplier proposal a week later. Called by the daily tick.
   */
  checkOfficeUnlock: () => {
    const state = get();
    if (state.officeUnlockedAt != null) return;
    if (!isOfficeUnlocked(state)) return;
    const today = state.totalDays || 0;
    set({
      officeUnlockedAt: today,
      nextOfficeProposalDay: today + FIRST_PROPOSAL_DELAY_DAYS,
    });
    // Fire a heads-up message so the player knows the bureau exists.
    try {
      const push = get().pushCustomMessage;
      if (typeof push === 'function') {
        push({
          senderType: 'narrator',
          senderName: 'Direction Générale',
          tone: 'friendly',
          kind: 'info',
          subject: '🏢 Votre siège social est inauguré',
          body:
            "Félicitations — votre investissement dans le siège social est finalisé : vos équipes support disposent " +
            "enfin de vraies conditions de travail (loyer et charges mensuelles désormais actifs). Au fil du temps, " +
            "vous recevrez des propositions de fournisseurs pour équiper l'espace (ménage, café, salle de repos, " +
            "tickets-restaurant…). Chaque équipement ajoute une charge mensuelle mais améliore la satisfaction de vos équipes.",
          ttlDays: 30,
          payload: { kind: 'office_unlock' },
        });
      }
    } catch (err) { console.warn('[office] unlock msg push failed:', err); }
  },

  /**
   * Called daily by the tick. If the office is unlocked, the cooldown
   * has elapsed, and at least one upgrade remains unproposed → send a
   * supplier email. Once accepted, the upgrade lands in officeUpgrades.
   */
  maybeFireOfficeProposal: () => {
    const state = get();
    if (state.officeUnlockedAt == null) return;
    const today = state.totalDays || 0;
    if (state.nextOfficeProposalDay == null || today < state.nextOfficeProposalDay) return;
    const upgrade = pickNextOfficeProposal(state.officeUpgrades || {});
    if (!upgrade) {
      // No more upgrades to propose — stop the scheduler.
      set({ nextOfficeProposalDay: null });
      return;
    }
    // Push the proposal as an actionable message. The Messagerie UI uses
    // `payload.kind` to render a custom CTA that calls back into
    // `acceptOfficeUpgrade(upgradeId)`.
    try {
      const push = get().pushCustomMessage;
      if (typeof push === 'function') {
        push({
          senderType: 'supplier',
          senderName: upgrade.supplier,
          tone: 'neutral',
          kind: 'office_proposal',
          subject: `${upgrade.icon} Proposition commerciale — ${upgrade.name}`,
          body:
            `Bonjour,\n\n${upgrade.description}\n\n` +
            `Coût d'installation : ${upgrade.costBuy.toLocaleString('fr-FR')} €\n` +
            `Abonnement mensuel : ${upgrade.chargeMonthly.toLocaleString('fr-FR')} €/mois\n` +
            `Effet attendu : +${upgrade.satisfactionBoost} pt(s) de satisfaction pour toute l'équipe (chauffeurs et support).\n\n` +
            "Nous restons à votre disposition pour démarrer dès cette semaine.",
          ttlDays: 21,
          payload: { kind: 'office_proposal', upgradeId: upgrade.id },
        });
      }
    } catch (err) { console.warn('[office] proposal push failed:', err); }

    // Schedule the next proposal with some jitter so the cadence feels
    // organic rather than mechanical.
    const jitter = Math.floor(Math.random() * (2 * PROPOSAL_RANDOM_JITTER_DAYS + 1)) - PROPOSAL_RANDOM_JITTER_DAYS;
    set({ nextOfficeProposalDay: today + PROPOSAL_COOLDOWN_DAYS + jitter });
  },

  /**
   * Accept an office upgrade proposal. Pays the install cost, adds the
   * upgrade to the owned list, schedules the next proposal earlier if
   * none is queued. Returns { ok, reason? } so the UI can surface errors.
   */
  acceptOfficeUpgrade: (upgradeId) => {
    const state = get();
    const upgrade = OFFICE_UPGRADE_BY_ID[upgradeId];
    if (!upgrade) return { ok: false, reason: 'unknown_upgrade' };
    if ((state.officeUpgrades || {})[upgradeId]) return { ok: false, reason: 'already_owned' };
    if ((state.treasury || 0) < upgrade.costBuy) return { ok: false, reason: 'insufficient_funds' };

    const today = state.totalDays || 0;
    set({
      treasury: (state.treasury || 0) - upgrade.costBuy,
      // 29/05/2026 — office upgrades count toward the CFE-exoneration
      // CAPEX threshold (50 000€/year) just like regular CAPEX.
      lifetimeCapexSpent: (state.lifetimeCapexSpent || 0) + (upgrade.costBuy || 0),
      officeUpgrades: {
        ...(state.officeUpgrades || {}),
        [upgradeId]: { boughtAt: today },
      },
    });
    return { ok: true };
  },

  // ---- aggregators ----
  getOfficeMonthlyCost: () => getOfficeUpgradesMonthlyCost(get().officeUpgrades || {}),
  getOfficeSatisfactionBoost: () => getOfficeSatisfactionBoost(get().officeUpgrades || {}),
  isOfficeUnlocked: () => isOfficeUnlocked(get()),
  getOfficeUpgradesCatalog: () => OFFICE_UPGRADES,
});
